import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Tooltip,
	useMediaQuery,
	useTheme,
	Fade,
	Skeleton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useHeaderMetricsSettings } from "../../contexts/HeaderMetricsSettingsContext";
import { matchesGroupFilter } from "../general-components/groupselector";
import { selectVisibleAnimalCount } from "../../utils/animalUtils";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

const MetricItem = ({ label, value, unit = "", trend, tooltip }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const { t } = useTranslation();
	const isValuePresent = value !== null && value !== undefined && value !== "" && (typeof value !== "number" || Number.isFinite(value));
	const displayValue = isValuePresent ? value : t("not-available", { defaultValue: "N/A" });
	const displayUnit = isValuePresent ? unit : "";

	return (
		<Tooltip title={tooltip || label}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					mx: 1, // Reduced margin
					cursor: "default",
					minWidth: isMobile ? "auto" : "60px", // Reduced min-width
				}}
			>
				<Typography
					variant="caption"
					sx={{
						color: "rgba(255, 255, 255, 0.7)",
						fontSize: "0.65rem", // Reduced font size
						textTransform: "uppercase",
						letterSpacing: "0.5px",
						whiteSpace: "nowrap",
						lineHeight: 1.1,
					}}
				>
					{label}
				</Typography>
				<Box display="flex" alignItems="center">
					<Typography
						variant="body2"
						sx={{
							color: "#fff",
							fontWeight: 600, // Slightly reduced weight
							fontSize: "0.85rem", // Reduced font size
							lineHeight: 1.2,
						}}
					>
						{displayValue}
						{displayUnit && <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.8, fontSize: "0.65rem" }}>{displayUnit}</Typography>}
					</Typography>
					{trend && (
						<Box
							sx={{
								ml: 0.5,
								display: "flex",
								color:
									trend === "up"
										? "#4caf50"
										: trend === "down"
										? "#f44336"
										: "rgba(255,255,255,0.5)",
							}}
						>
							{trend === "up" ? (
								<TrendingUpIcon sx={{ fontSize: 12 }} /> // Reduced icon size
							) : trend === "down" ? (
								<TrendingDownIcon sx={{ fontSize: 12 }} /> // Reduced icon size
							) : (
								<TrendingFlatIcon sx={{ fontSize: 12 }} /> // Reduced icon size
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Tooltip>
	);
};

const HeaderMetrics = () => {
	const { t } = useTranslation();
	const { currentUser } = useAuth();
	const { settings, loading: settingsLoading } = useHeaderMetricsSettings();
	const { enabled, selectedMetrics } = settings;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	// Also hide on tablet if too many items, but for now just hide on mobile
	
	const [dataLoading, setDataLoading] = useState(false);
	const [metrics, setMetrics] = useState(null);
	const [rawData, setRawData] = useState(null);
	const [dataLoaded, setDataLoaded] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState(() => {
		return sessionStorage.getItem("dashboardSelectedGroup") || "all";
	});

	// Listen for dashboard group changes
	useEffect(() => {
		const handleGroupChange = (e) => {
			setSelectedGroup(e.detail?.group || "all");
		};
		window.addEventListener("dashboardGroupChange", handleGroupChange);
		return () => window.removeEventListener("dashboardGroupChange", handleGroupChange);
	}, []);

	// Only fetch if enabled and user is logged in
	useEffect(() => {
		let isMounted = true;

		async function fetchData() {
			if (!currentUser?.uid || !enabled || settingsLoading) return;
			
			// If we already have data and just re-rendering, don't fetch again immediately
			// unless we want to support refresh interval (omitted for now to save reads)
			if (dataLoaded) return;

			setDataLoading(true);
			try {
				// CalfHealth: load animals for header metrics
				const { getAnimals } = await import("../../api/firestore/services/animalService");
				const animalList = await getAnimals(currentUser.uid).catch(() => []);
				const erp = { animals: animalList };
				const boar = {};

				if (isMounted) {
					setRawData({ erp, boar });
					setDataLoaded(true);
				}
			} catch (error) {
				console.error("[HeaderMetrics] Error loading data:", error);
			} finally {
				if (isMounted) setDataLoading(false);
			}
		}

		if (enabled && !dataLoaded) {
			fetchData();
		}
	}, [currentUser?.uid, enabled, settingsLoading, dataLoaded]);

	// Recalculate metrics when raw data or selected group changes
	useEffect(() => {
		if (!rawData) return;

		const { erp, boar } = rawData;

		// Filter ERP data by group if a group is selected
		let filteredErp = erp;
		let filteredBoar = boar;
		if (selectedGroup && selectedGroup !== "all") {
			const animals = erp?.animals || [];
			const filteredAnimalMACs = new Set(
				animals
					.filter((a) => matchesGroupFilter(a, selectedGroup))
					.map((a) => a.MAC)
					.filter(Boolean)
			);

			const filterByMAC = (records) =>
				(records || []).filter((r) => filteredAnimalMACs.has(r.MAC));

			filteredErp = {
				...erp,
				animals: animals.filter((a) => matchesGroupFilter(a, selectedGroup)),
				births: filterByMAC(erp?.births),
				inseminations: filterByMAC(erp?.inseminations),
				pregnancyChecks: filterByMAC(erp?.pregnancyChecks),
				generalEvents: filterByMAC(erp?.generalEvents),
				weanings: filterByMAC(erp?.weanings),
				returnToEstrus: filterByMAC(erp?.returnToEstrus),
				stockEvents: filterByMAC(erp?.stockEvents),
			};

			// Filter boar data by MAC if applicable
			const boarAnimals = boar?.animals || [];
			const filteredBoarMACs = new Set(
				boarAnimals
					.filter((a) => matchesGroupFilter(a, selectedGroup))
					.map((a) => a.MAC)
					.filter(Boolean)
			);
			filteredBoar = {
				...boar,
				animals: boarAnimals.filter((a) => matchesGroupFilter(a, selectedGroup)),
				collections: (boar?.collections || []).filter((r) => filteredBoarMACs.has(r.MAC)),
				qc: (boar?.qc || []).filter((r) => filteredBoarMACs.has(r.MAC)),
			};
		}

		// CalfHealth header KPIs — computed from local animal data.
		// The non-archived rule lives in utils/animalUtils so this bar and the
		// dashboard's herd tile always report the same number.
		const animals = filteredErp?.animals || [];
		const kpis = {
			activeAnimals: selectVisibleAnimalCount(animals),
			totalAnimals: animals.length,
		};
		const comprehensive = {};
		setMetrics({ kpis, comprehensive });
	}, [rawData, selectedGroup]);

	// Reset data loaded state if user changes (logout/login)
	useEffect(() => {
		setDataLoaded(false);
		setRawData(null);
	}, [currentUser?.uid]);

	if (settingsLoading || !enabled || isMobile || !selectedMetrics || selectedMetrics.length === 0) {
		return null;
	}

	if (dataLoading && !metrics) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: "100%",
					bgcolor: "rgba(0, 0, 0, 0.1)",
					py: 0.5,
					px: isMobile ? 1 : 2,
					borderBottomLeftRadius: "15px",
					borderBottomRightRadius: "15px",
					backdropFilter: "blur(4px)",
					borderTop: "1px solid rgba(255,255,255,0.1)",
				}}
			>
				{selectedMetrics.map((metricId, index) => (
					<Box key={metricId} sx={{ display: "flex", alignItems: "center" }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								mx: 1,
								minWidth: isMobile ? "auto" : "60px",
							}}
						>
							<Skeleton
								variant="text"
								width={40}
								height={12}
								sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
							/>
							<Skeleton
								variant="text"
								width={30}
								height={20}
								sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
							/>
						</Box>
						{index !== selectedMetrics.length - 1 && (
							<Box
								sx={{
									width: "1px",
									height: "16px",
									background: "rgba(255,255,255,0.15)",
									mx: 0.5,
								}}
							/>
						)}
					</Box>
				))}
			</Box>
		);
	}

	if (!metrics) return null;

	const { kpis, comprehensive } = metrics;

	// CalfHealth header metrics
	const getMetricValue = (id) => {
		switch (id) {
			case "activeAnimals":
				return { 
					label: t("active", { defaultValue: "Active" }), 
					value: kpis?.activeAnimals || 0,
					tooltip: t("total-active-animals", { defaultValue: "Total active animals" })
				};
			default:
				return null;
		}
	};

	return (
		<Fade in={true}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: "100%",
					bgcolor: "rgba(0, 0, 0, 0.1)",
					py: 0.5,
					px: isMobile ? 1 : 2,
					borderBottomLeftRadius: "15px",
					borderBottomRightRadius: "15px",
					backdropFilter: "blur(4px)",
					borderTop: "1px solid rgba(255,255,255,0.1)",
					overflowX: "auto",
					"&::-webkit-scrollbar": { display: "none" },
					msOverflowStyle: "none",
					scrollbarWidth: "none",
				}}
			>
				{selectedMetrics.map((metricId) => {
					const data = getMetricValue(metricId);
					if (!data) return null;
					return (
						<Box key={metricId} sx={{ display: "flex", alignItems: "center" }}>
							<MetricItem
								label={data.label}
								value={data.value}
								unit={data.unit}
								tooltip={data.tooltip}
							/>
							{/* Add separator if not last */}
							{selectedMetrics.indexOf(metricId) !== selectedMetrics.length - 1 && (
								<Box
									sx={{
										width: "1px",
										height: "16px", // Smaller separator
										background: "rgba(255,255,255,0.15)",
										mx: 0.5,
									}}
								/>
							)}
						</Box>
					);
				})}
			</Box>
		</Fade>
	);
};

export default HeaderMetrics;

